import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ScrollArea,
  TextInput,
  Title,
  Switch,
  Divider,
  Group,
  Stack,
  Modal,
  Text,
  ActionIcon
} from '@mantine/core';
import CloseIcon from '@mui/icons-material/Close';
import { Plus, Minus } from 'tabler-icons-react';
import { TransformerType, allmenuOption } from './mdmUtils';
import { setMdmCurrentStep, setSelectedCardId, updateCards } from '../../stateManagement/actions/mdmDashboard';
import { useSelector } from 'react-redux';
import { openPopup } from '../../stateManagement/actions/popupActions';
import { uploadDocument } from '../../services/bankingService';
import { useId } from 'react';
import { ConfirmationPopUp } from '../../components/popup/confirmationPopUp';
import { Loader } from '../../components/loader/Loader';
import { FormControlLabel, Tooltip } from '@mui/material';

const ExcelUpload = ({
  onFileUpload,
  isUploading,
  uploadError,
  currentValue,
}: {
  onFileUpload: (file: File) => void;
  isUploading: boolean;
  uploadError: string | null;
  currentValue: string;
}) => {
  const inputId = useId(); // Unique ID for each component instance
  const handleDownload = () => {
    if (currentValue) {
      const link = document.createElement('a');
      link.href = currentValue;
      link.download = 'template.xlsx';
      link.click();
    }
  };

  return (
    <Box>
      {uploadError && <Text color="red" size="xs">{uploadError}</Text>}
      <TextInput
        label="Excel Template File"
        value={currentValue}
        readOnly
        rightSection={
          <input
            type="file"
            accept=".xls,.xlsx"
            style={{ display: 'none' }}
            id={inputId}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                onFileUpload(e.target.files[0]);
                e.target.value = '';
              }
            }}
          />
        }
        rightSectionWidth={100}
      />
      <div style={{ gap: "10px", display: "flex" }}>
        <label htmlFor={inputId}>
          <Button size="xs" component="span" loading={isUploading} style={{ marginTop: 6 }}>
          Upload Template (.xlsx)
          </Button>
        </label>
        <Button
          size="xs"
          style={{ marginTop: 6 }}
          onClick={handleDownload}
          disabled={!currentValue}
        >
          Download
        </Button>
      </div>
    </Box>
  );
};

const MdmSetUp = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isUploadingModal, setIsUploadingModal] = useState(false);
  const [uploadErrorModal, setUploadErrorModal] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const cardsRedux = useSelector((state: any) => state.mdmDashboardReducer.cards);
  const [cards, setCards] = useState<any[]>(cardsRedux);
  const selectedCardIdFromStore = useSelector((state: any) => state.mdmDashboardReducer.selectedCardId);
  const selectedCardId = selectedCardIdFromStore ?? cards?.[0]?.id ?? null;
  const [modalOpened, setModalOpened] = useState(false);
  
  const [newCard, setNewCard] = useState({
    name: '',
    entityName: '',
    templateUrl: '',
    postAPI: '',
    getAPI: '',
    configAPI: '',
    mandatory: false,
    topicName: '',
    preserveOnFailure: false,
    menuOption: [] as { id: string; label: string }[],
    transformer: [
      {
        entityName: '',
        transformerId: '',
        operationType: '',
        preprocessValidationExcludeGroup: '',
      }
    ],
  });
  const selectedCard = cards.find((card: any) => card.id === selectedCardId);
  const [confirmationPopUp, setConfirmationPopUp] = useState(false);
  const [pendingDeleteCardId, setPendingDeleteCardId] = useState<string | null>(null);
  useEffect(() => {
    setLoading(true);
    setCards(cardsRedux);
    setLoading(false);
  }, [cardsRedux]);

  const validateCard = (card: any): string | null => {
    if (!card.name?.trim()) return 'Name is required';
    if (!card.entityName?.trim()) return 'Entity Name is required';
    if (!card.templateUrl?.trim()) return 'Excel Template is required';
    // if (!card.topicName?.trim()) return 'Topic Name is required';
    if (!card.getAPI?.trim()) return 'GET API is required';
    if (!card.postAPI?.trim()) return 'POST API is required';
    // if (!card.menuOption || card.menuOption.length === 0) return 'At least one Menu Option must be selected';

    if (!card.transformer || card.transformer.length === 0) {
      return 'At least one Transformer is required';
    }

    for (let i = 0; i < card.transformer.length; i++) {
      const t = card.transformer[i];
      if (!t.entityName?.trim()) return `Transformer ${i + 1}: Entity Name is required`;
      if (!t.transformerId?.trim()) return `Transformer ${i + 1}: Transformer ID is required`;
    }

    return null;
  };

  const onMenuOptionChange = (
    index: number,
    option: { id: string; label: string },
    checked: boolean
  ) => {
    setCards((prev) => {
      const updated = [...prev];
      const currentOptions = updated[index].menuOption || [];

      let newOptions;
      if (checked) {
        const exists = currentOptions.some((opt: { id: string; }) => opt.id === option.id);
        newOptions = exists ? currentOptions : [...currentOptions, option];
      } else {
        newOptions = currentOptions.filter((opt: { id: string; }) => opt.id !== option.id);
      }

      updated[index] = {
        ...updated[index],
        menuOption: newOptions,
      };

      return updated;
    });
  };

  const handleExcelFileUpload = async (file: File) => {
    if (!file) {
      if (isUploadingModal) setIsUploadingModal(false);
      openPopup('Alert', 'Please upload a file');
      return;
    }

    const formData = new FormData();
    formData.append('files', file, file.name);

    try {
      setIsUploadingModal(true);
      setUploadErrorModal(null);

      const fileUploadResponse = await uploadDocument(formData);
      const blobKey: string = JSON.parse(fileUploadResponse[0].body).features[0].Uri;

      setNewCard((prev) => ({
        ...prev,
        templateUrl: blobKey,
      }));
    } catch (error: any) {
      setUploadErrorModal(error.message || 'Unknown error');
    } finally {
      setIsUploadingModal(false);
    }
  };
  const handleExcelFileUploadForSelectedCard = async (file: File) => {
    if (!file) {
      if (isUploading) setIsUploading(false);
      openPopup('Alert', 'Please upload a file');
      return;
    }

    const formData = new FormData();
    formData.append('files', file, file.name);

    try {
      setIsUploading(true);
      setUploadError(null);

      const fileUploadResponse = await uploadDocument(formData);
      const blobKey: string = JSON.parse(fileUploadResponse[0].body).features[0].Uri;

      setCards((prev) =>
        prev.map((card) =>
          card.id === selectedCardId
            ? { ...card, templateUrl: blobKey }
            : card
        )
      );
    } catch (error: any) {
      setUploadError(error.message || 'Unknown error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (id: string) => {
    const cardToDelete = cards.find((card) => card.id === id);
    if (cardToDelete?.mandatory) {
      openPopup('Alert', 'This card is mandatory and cannot be deleted.');
      return;
    }
    const updatedCards = cards.filter((card) => card.id !== id);
    setCards(updatedCards);
    if (selectedCardId === id) {
      setSelectedCardId(updatedCards.length > 0 ? updatedCards[0].id : null);
    }
  };

  const handleChange = (field: keyof any, value: any) => {
    setCards((prev) =>
      prev.map((card) =>
        card.id === selectedCardId ? { ...card, [field]: value } : card
      )
    );
  };

  const handleTransformerChange = (index: number, field: keyof TransformerType, value: string) => {
    setCards((prev) =>
      prev.map((card) => {
        if (card.id !== selectedCardId) return card;
        const updatedTransformers = [...card.transformer];
        if (index === card.transformer.length) {
          updatedTransformers.push({
            entityName: '',
            transformerId: '',
            operationType: '',
            preprocessValidationExcludeGroup: '',
          });
        } else {
          updatedTransformers[index] = { ...updatedTransformers[index], [field]: value };
        }
        return { ...card, transformer: updatedTransformers };
      })
    );
  };

  const handleDeleteTransformer = (index: number) => {
    setCards((prev) =>
      prev.map((card) => {
        if (card.id !== selectedCardId) return card;
        const updatedTransformers = card.transformer.filter((_: any, idx: number) => idx !== index);
        return { ...card, transformer: updatedTransformers };
      })
    );
  };

  const handleAddCard = () => {
    const cardWithId = {
      ...newCard,
      id: `card-${Date.now()}`,
    };
    const error = validateCard(cardWithId);
    if (error) {
      openPopup("Alert", error);
      return;
    }
    const newCardId = `card-${Date.now()}`;
    setCards([
      ...cards,
      {
        ...newCard,
        id: newCardId,
      }
    ]);
    setSelectedCardId(newCardId);
    setModalOpened(false);
    setNewCard({
      entityName: '',
      name: '',
      templateUrl: '',
      postAPI: '',
      getAPI: '',
      configAPI: '', 
      mandatory: false,
      menuOption: [],
      topicName: '',
      preserveOnFailure: false,
      transformer: [
        {
          entityName: '',
          transformerId: '',
          operationType: '',
          preprocessValidationExcludeGroup: '',
        }
      ]
    });
  };
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box style={{ display: 'flex', height: '100%', padding: "0.5rem", gap: "20px" }}>
          {/* Left Column - Card List */}
          <Box style={{ width: 250, height: '100%', position: 'relative' }}>
            <ScrollArea
              type="always"
              style={{
                height: '90%',
                border: '1px solid #ccc',
                borderRadius: 6,
                overflowX: 'hidden',
              }}
            >
              {cards.length > 0 ? cards.map((card) => (
                <Box
                  key={card.id}
                  p="sm"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: selectedCardId === card.id ? '#e3f2fd' : undefined,
                    borderBottom: '1px solid #ddd',
                    width: '100%',
                    minWidth: 0,
                  }}
                  onClick={() => setSelectedCardId(card.id)}
                >
                  <Box
                    style={{
                      flex: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 170,
                      minWidth: 0,
                    }}
                  >
                    {card.name}
                  </Box>
                    <div>
                    {card.mandatory ? (
                      <Tooltip title="This card is mandatory and cannot be deleted">
                      <div>
                        <Button
                        variant="subtle"
                        color="gray"
                        size="xs"
                        style={{
                          padding: 4,
                          minWidth: 24,
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid gray',
                          marginRight: '5px',
                        }}
                        onClick={(e: any) => e.stopPropagation()}
                        disabled={true}
                        >
                        <CloseIcon
                          fontSize="small"
                          style={{ pointerEvents: 'none' }}
                        />
                        </Button>
                      </div>
                      </Tooltip>
                    ) : (
                      <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      style={{
                        padding: 4,
                        minWidth: 24,
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid red',
                        marginRight: '5px',
                      }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setPendingDeleteCardId(card.id);
                        setConfirmationPopUp(true);
                      }}
                      >
                      <CloseIcon
                        fontSize="small"
                        style={{ pointerEvents: 'none' }}
                      />
                      </Button>
                    )}
                    </div>
                </Box>
              )) : (
                <Box
                  p="sm"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    color: '#666',
                    fontStyle: 'italic'
                  }}
                >
                  No cards available. Click "Create New Card" to get started.
                </Box>
              )}
            </ScrollArea>
            <Box
              style={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                padding: '0.5rem',
                background: 'white',
              }}
            >
              <Button onClick={() => {
                setModalOpened(true);
                setNewCard({
                  name: '',
                  entityName: '',
                  templateUrl: '',
                  postAPI: '',
                  getAPI: '',
                  configAPI: '', 
                  mandatory: false,
                  topicName: '',
                  preserveOnFailure: false,
                  menuOption: [] as { id: string; label: string }[],
                  transformer: [
                    {
                      entityName: '',
                      transformerId: '',
                      operationType: '',
                      preprocessValidationExcludeGroup: ''
                    }
                  ],
                });
              }}>Create New Card</Button>
            </Box>
          </Box>

          {/* Right Column - Card Details */}
          <Box
            style={{
              flexGrow: 1,
              padding: '1rem',
              border: '1px solid #ccc',
              borderRadius: 8,
              overflowY: 'auto',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {selectedCard ? (
              <>
                <Title order={4} mb="md">Edit Card: {selectedCard.name}</Title>
                <Stack spacing="sm">
                  <TextInput
                    label="Entity Name"
                    value={selectedCard.entityName}
                    onChange={(e) => handleChange('entityName', e.currentTarget.value)}
                  />
                  <TextInput
                    label="Master Display Name"
                    value={selectedCard.name}
                    onChange={(e) => handleChange('name', e.currentTarget.value)}
                  />
                  <ExcelUpload
                    currentValue={selectedCard.templateUrl}
                    onFileUpload={handleExcelFileUploadForSelectedCard}
                    isUploading={isUploading}
                    uploadError={uploadError}
                  />
                  <TextInput
                    label="GET API"
                    value={selectedCard.getAPI}
                    onChange={(e) => handleChange('getAPI', e.currentTarget.value)}
                  />
                  <TextInput
                    label="POST API"
                    value={selectedCard.postAPI}
                    onChange={(e) => handleChange('postAPI', e.currentTarget.value)}
                  />
                    <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    }}>
                    <Switch
                      label="Mandatory"
                      checked={selectedCard.mandatory}
                      disabled={
                        cardsRedux.find((card: any) => card.id === selectedCardId)?.mandatory === true
                      }                      
                      onChange={(e) => handleChange('mandatory', e.currentTarget.checked)}
                    />
                  </div>
                  {/* <Divider label="Menu Options" my="sm" />
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    {allmenuOption.map((option) => (
                      <Switch
                        key={option.id}
                        label={option.label}
                        checked={selectedCard?.menuOption?.some((opt: { id: string; }) => opt.id === option.id)}
                        onChange={(event) =>
                          onMenuOptionChange(
                            cards.findIndex((card) => card.id === selectedCardId),
                            { id: option.id, label: option.label },
                            event.currentTarget.checked
                          )
                        }
                      />
                    ))}
                  </div> */}
                  <TextInput
                    label="Topic Name"
                    value={selectedCard?.topicName ?? ''}
                    onChange={(e) => handleChange('topicName', e.currentTarget.value)}
                  />
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}>
                    <Switch
                      label="Preserve On Failure"
                      checked={selectedCard?.preserveOnFailure || false}
                      onChange={(e) => handleChange('preserveOnFailure', e.target.checked)}
                    />
                  </div>
                  <Divider label="Transformer Details" my="sm" />
                  {selectedCard.transformer.map((t: any, idx: number) => (
                    <Box key={idx} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: 6 }}>
                      <Group position="apart" style={{ marginBottom: '1rem' }}>
                        <Title order={5} style={{ marginBottom: '0' }}>Transformer {idx + 1}</Title>
                        <ActionIcon
                          color="red"
                          size="lg"
                          variant="outline"
                          onClick={() => handleDeleteTransformer(idx)}
                          disabled={selectedCard.transformer.length <= 1}
                        >
                          <Minus size={16} color='red' />
                        </ActionIcon>
                      </Group>
                      <Stack spacing="sm">
                        <TextInput
                          label="Entity Name"
                          value={t.entityName}
                          onChange={(e) => handleTransformerChange(idx, 'entityName', e.currentTarget.value)}
                        />
                        <TextInput
                          label="Transformer ID"
                          value={t.transformerId}
                          onChange={(e) => handleTransformerChange(idx, 'transformerId', e.currentTarget.value)}
                        />
                        <TextInput
                          label="Operation Type"
                          value={t.operationType}
                          onChange={(e) => handleTransformerChange(idx, 'operationType', e.currentTarget.value)}
                        />
                        <TextInput
                          label="Preprocess Validation Exclude Group"
                          value={t.preprocessValidationExcludeGroup || ''}
                          onChange={(e) =>
                            handleTransformerChange(idx, 'preprocessValidationExcludeGroup', e.currentTarget.value)
                          }
                        />
                      </Stack>
                    </Box>
                  ))}
                  <ActionIcon
                    color="blue"
                    size="lg"
                    variant="outline"
                    onClick={() =>
                      handleTransformerChange(selectedCard.transformer.length, 'entityName', '')
                    }
                    style={{ alignSelf: 'flex-end' }}
                  >
                    <Plus size={16} color='blue' />
                  </ActionIcon>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      style={{ width: 'fit-content' }}
                      onClick={() => {
                        for (let i = 0; i < cards.length; i++) {
                          const error = validateCard(cards[i]);
                          if (error) {
                            openPopup("Alert", `Card ${i + 1}: ${error}`);
                            return;
                          }
                        };
                        updateCards(cards);
                        setMdmCurrentStep(2);
                      }}
                    >
                      Save and Next
                    </Button>
                  </div>
                </Stack>
              </>
            ) : (
              <></>
            )}

            <Group position="right" style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}>
              {/* <Button onClick={() => setMdmCurrentStep(1)}>Back</Button> */}
            </Group>

          </Box>

          {/* Modal for Adding New Card */}
          <Modal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            title="Add New Card"
            size="lg"
          >
            <Stack style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <TextInput
                label="Entity Name"
                value={newCard.entityName}
                onChange={(e) => setNewCard({ ...newCard, entityName: e.currentTarget.value })}
              />
              <TextInput
                label="Card Name"
                value={newCard.name}
                onChange={(e) => setNewCard({ ...newCard, name: e.currentTarget.value })}
              />
              {modalOpened && (
                <ExcelUpload
                  currentValue={newCard.templateUrl}
                  onFileUpload={handleExcelFileUpload}
                  isUploading={isUploadingModal}
                  uploadError={uploadErrorModal}
                />
              )}
              <TextInput
                label="GET API"
                value={newCard.getAPI}
                onChange={(e) => setNewCard({ ...newCard, getAPI: e.currentTarget.value })}
              />
              <TextInput
                label="POST API"
                value={newCard.postAPI}
                onChange={(e) => setNewCard({ ...newCard, postAPI: e.currentTarget.value })}
              />
              <TextInput
                label="Config API"
                value={newCard.configAPI}
                onChange={(e) => setNewCard({ ...newCard, configAPI: e.currentTarget.value })}
              />
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
              }}>
                <Switch
                  label="Mandatory"
                  checked={newCard.mandatory}
                  onChange={(e) => setNewCard({ ...newCard, mandatory: e.currentTarget.checked })}
                />
              </div>
              {/* <Divider label="Menu Options" my="sm" />
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                {allmenuOption.map((option) => (
                  <Switch
                    key={option.id}
                    label={option.label}
                    checked={newCard.menuOption.some((opt) => opt.id === option.id)}
                    onChange={(e) => {
                      const isChecked = e.currentTarget.checked;
                      setNewCard((prev) => {
                        const currentOptions = prev.menuOption || [];
                        let newOptions;
                        if (isChecked) {
                          const exists = currentOptions.some((opt) => opt.id === option.id);
                          newOptions = exists ? currentOptions : [...currentOptions, option];
                        } else {
                          newOptions = currentOptions.filter((opt) => opt.id !== option.id);
                        }
                        return { ...prev, menuOption: newOptions };
                      });
                    }}
                  />
                ))}
              </div> */}
              <TextInput
                label="Topic Name"
                value={newCard.topicName}
                onChange={(e) => setNewCard({ ...newCard, topicName: e.currentTarget.value })}
              />
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
              }}>
                <Switch
                  label="Preserve on Failure"
                  checked={newCard.preserveOnFailure}
                  onChange={(e) =>
                    setNewCard({ ...newCard, preserveOnFailure: e.target.checked })
                  }
                  color="primary"
                />
              </div>
              <Divider label="Transformer Details" my="sm" />
              {newCard.transformer.map((t: any, idx: number) => (
                <Box key={idx} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: 6 }}>
                  <Group position="apart" style={{ marginBottom: '1rem' }}>
                    <Title order={5}>Transformer {idx + 1}</Title>
                    <ActionIcon
                      color="red"
                      size="lg"
                      variant="outline"
                      onClick={() => {
                        setNewCard((prev) => {
                          const updatedTransformers = prev.transformer.filter((_, id) => id !== idx);
                          return { ...prev, transformer: updatedTransformers };
                        });
                      }}
                      disabled={newCard.transformer.length <= 1}
                    >
                      <Minus size={16} color='blue' />
                    </ActionIcon>
                  </Group>
                  <Stack spacing="sm">
                    <TextInput
                      label="Entity Name"
                      value={t.entityName}
                      onChange={(e) => setNewCard({
                        ...newCard,
                        transformer: newCard.transformer.map((transformer, i) =>
                          i === idx ? { ...transformer, entityName: e.currentTarget.value } : transformer),
                      })}
                    />
                    <TextInput
                      label="Transformer ID"
                      value={t.transformerId}
                      onChange={(e) => setNewCard({
                        ...newCard,
                        transformer: newCard.transformer.map((transformer, i) =>
                          i === idx ? { ...transformer, transformerId: e.currentTarget.value } : transformer),
                      })}
                    />
                    <TextInput
                      label="Operation Type"
                      value={t.operationType}
                      onChange={(e) => setNewCard({
                        ...newCard,
                        transformer: newCard.transformer.map((transformer, i) =>
                          i === idx ? { ...transformer, operationType: e.currentTarget.value } : transformer),
                      })}
                    />
                    <TextInput
                      label="Preprocess Validation Exclude Group"
                      value={t.preprocessValidationExcludeGroup}
                      onChange={(e) => setNewCard({
                        ...newCard,
                        transformer: newCard.transformer.map((transformer, i) =>
                          i === idx ? { ...transformer, preprocessValidationExcludeGroup: e.currentTarget.value } : transformer),
                      })}
                    />
                  </Stack>
                </Box>
              ))}
              <ActionIcon
                color="blue"
                size="lg"
                variant="outline"
                onClick={() =>
                  setNewCard({
                    ...newCard,
                    transformer: [
                      ...newCard.transformer,
                      { entityName: '', transformerId: '', operationType: '', preprocessValidationExcludeGroup: '' },
                    ],
                  })
                }
              >
                <Plus size={16} />
              </ActionIcon>
            </Stack>
            <Button
              fullWidth
              onClick={handleAddCard}
              color="blue"
              style={{ marginTop: '1rem' }}
            >
              Add Card
            </Button>
          </Modal>
          <ConfirmationPopUp
            openConfirmModal={confirmationPopUp}
            message="Are you sure you want to delete the card?"
            setOpenConfirmModal={() => {
              setConfirmationPopUp(false);
            }}
            successMethod={() => {
              if (pendingDeleteCardId) {
                handleDelete(pendingDeleteCardId);
              };
              setPendingDeleteCardId(null);
            }}
          />
        </Box>
      )}
    </>
  );
};

export default MdmSetUp;